if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
        this.stickyBar = this.querySelector('product-sticky');
        this.submitButton = this.querySelector('.product-form__submit');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initReadMore(); 

        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      initReadMore() {
        const desc = this.querySelector("[data-readmore]");
        if (!desc) return;

        const btn = this.querySelector(".readmore-btn");
        if (!btn) return;

        const lineClamp = 2;
        const lineHeight = parseFloat(getComputedStyle(desc).lineHeight);
        const collapsedHeight = lineHeight * lineClamp;
        const expandedHeight = desc.scrollHeight;

        desc.style.maxHeight = collapsedHeight + "px";

        if (expandedHeight > collapsedHeight) {
          btn.classList.remove("hidden");
        } else {
          btn.classList.add("hidden");
        }

        btn.addEventListener("click", () => {
          const isExpanded = desc.classList.toggle("expanded");

          if (isExpanded) {
            desc.style.maxHeight = expandedHeight + "px";
            btn.textContent = window.MessageEvent.readLess;
          } else {
            desc.style.maxHeight = collapsedHeight + "px";
            btn.textContent = window.MessageEvent.readMore;
          }
        });
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.submitButton.querySelector('.loading__spinner').classList.remove('hidden');
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          const newQuotaEl = html.querySelector('.quota-data');
          const oldQuotaEl = this.querySelector('.quota-data');
          if (newQuotaEl && oldQuotaEl) {
            oldQuotaEl.textContent = newQuotaEl.textContent;
          }

          const newSticky = html.querySelector('product-sticky');
          const oldSticky = this.querySelector('product-sticky');
          if (newSticky && oldSticky) {
            oldSticky.replaceWith(newSticky);
          }

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          const submitBtnEl = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          let isDisabled = !!submitBtnEl?.hasAttribute('disabled');
          let message = isDisabled ? (window.variantStrings?.soldOut || 'Sold out') : null;

          // const quotaEl = this.querySelector('.quota-data');
          // if (quotaEl) {
          //   try {
          //     const quota = JSON.parse(quotaEl.textContent);
          //     if (quota && quota.exceed) {
          //       isDisabled = true;
          //       message = 'Limit Reached';
          //     }
          //   } catch (e) {
          //     console.error('Quota parse error', e);
          //   }
          // }

          this.productForm?.toggleSubmitButton(isDisabled, message);
          this.submitButton.setAttribute('aria-disabled', 'false');
          this.submitButton.classList.remove('loading');
          this.submitButton.querySelector('.loading__spinner').classList.add('hidden');

          document.dispatchEvent(new CustomEvent('recheck:quota'));

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                if (current) current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

if (!customElements.get('product-sticky')) {
  customElements.define("product-sticky", 
    class ProductSticky extends HTMLElement {
      constructor() {
        super();
        this.qtyInput = null;
        this.addBtn = null;
        this.stickyBar = null;
        this.mainAddBtn = null;

        this.unsubscribeVariant = null;
        this.unsubscribeQuantity = null;

        this._silentUpdate = false;
        this._recheckQuotaHandler = null;
      }

      connectedCallback() {
        this.qtyInput = this.querySelector(".product-sticky-qty");
        this.addBtn = this.querySelector(".product-sticky-add");
        this.stickyBar = this.querySelector(".product-sticky");
        this.mainAddBtn = document.querySelector("product-form form [type='submit']");

        if (this.mainAddBtn) this.initObserver();

        if (this.addBtn) {
          this.addBtn.addEventListener("click", (e) => {
            e.preventDefault();
            const id = this.dataset.variantId;
            const qty = parseInt(this.qtyInput?.value, 10) || 1;
            this.addToCart(id, qty);
          });
        }

        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
        this.unsubscribeQuantity = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.handleQuantityUpdate.bind(this));

        this._recheckQuotaHandler = (e) => {
          const exceed = e?.detail?.exceed || false;
          const variantInput = document.querySelector("product-form input[name='id']");
          const currentVariantId = variantInput ? variantInput.value : null;
          const variant = { id: currentVariantId, available: true };
        };
        document.addEventListener("recheck:quota", this._recheckQuotaHandler);

        this.bindQuantitySync();
        this.syncQuantityFromMain();
        this.syncButtonFromMain();
        this.observeMainButton();
      }

      disconnectedCallback() {
        this.unsubscribeVariant?.();
        this.unsubscribeQuantity?.();
        this._mainBtnObserver?.disconnect();
        if (this._recheckQuotaHandler) document.removeEventListener("recheck:quota", this._recheckQuotaHandler);
      }

      initObserver() {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // compute absolute top of the observed element
              const absoluteTop = entry.boundingClientRect.top + window.scrollY;
              // show sticky only after user scrolled *past* the mainAddBtn (and it's not intersecting)
              if (!entry.isIntersecting && window.scrollY > absoluteTop) {
                this.classList.add("showing");
              } else {
                this.classList.remove("showing");
              }
            });
          },
          { threshold: 0 }
        );

        observer.observe(this.mainAddBtn);
      }

      handleVariantChange(message) {
        const data = message?.data;
        if (!data) return;

        const { sectionId, variant, html } = data;

        if (sectionId != null && String(sectionId) !== String(this.dataset.section)) return;

        if (!variant) {
          this.syncButtonFromMain();
          return;
        }

        this.dataset.variantId = variant.id;

        if (html && typeof html.getElementById === "function") {
          try {
            const newPrice = html.getElementById(`price-${this.dataset.section}`);
            const stickyPrice = this.querySelector(".product-sticky-price");
            if (newPrice && stickyPrice) stickyPrice.innerHTML = newPrice.innerHTML;
          } catch (err) {
            console.warn("product-sticky: failed to update price from html", err);
          }
        }

        const stickyVariant = this.querySelector(".product-sticky-selected");
        if (stickyVariant) stickyVariant.textContent = variant.title ?? "";

        this.syncQuantityFromMain();
        this.syncButtonFromMain();
      }

      updateButtonState(variant, exceed = false) {
        const submitButton = this.querySelector(".product-sticky-add");
        if (!submitButton) return;
        const buttonText = submitButton.querySelector("span") || submitButton;

        if (!variant) {
          submitButton.setAttribute("disabled", "disabled");
          buttonText.textContent = window.variantStrings?.unavailable || "Unavailable";
          return;
        }

        if (!variant.available) {
          submitButton.setAttribute("disabled", "disabled");
          buttonText.textContent = window.variantStrings?.soldOut || "Sold out";
          return;
        }

        if (exceed) {
          // submitButton.setAttribute("disabled", "disabled");
          // buttonText.textContent = window.variantStrings?.limitReached || "Limit Reached";
          return;
        }

        submitButton.removeAttribute("disabled");
        submitButton.removeAttribute("aria-disabled");
        submitButton.classList.remove("disabled");
        buttonText.textContent = window.variantStrings?.addToCart || "Add to cart";
      }

      syncQuantityFromMain() {
        const mainQtyInput = document.querySelector("product-info .quantity__input");
        if (!mainQtyInput || !this.qtyInput) return;

        // copy attributes and value quietly
        this._silentUpdate = true;
        this.qtyInput.min = mainQtyInput.min ?? this.qtyInput.min ?? 1;

        if (mainQtyInput.hasAttribute("max")) {
          this.qtyInput.max = mainQtyInput.max;
        } else {
          this.qtyInput.removeAttribute("max");
        }

        this.qtyInput.step = mainQtyInput.step ?? this.qtyInput.step ?? 1;
        this.qtyInput.value = mainQtyInput.value ?? this.qtyInput.value ?? this.qtyInput.min ?? 1;
        this._silentUpdate = false;
      }

      syncQuantityToMain() {
        if (!this.qtyInput) return;
        if (this._silentUpdate) return;

        const qty = parseInt(this.qtyInput.value, 10) || 1;
        publish(PUB_SUB_EVENTS.quantityUpdate, { data: { quantity: qty } });
      }

      observeMainButton() {
        if (!this.mainAddBtn) return;

        const observer = new MutationObserver(() => {
          this.syncButtonFromMain();
        });

        observer.observe(this.mainAddBtn, {
          attributes: true,
          childList: true,
          characterData: true,
          subtree: true,
        });

        this._mainBtnObserver = observer;
      }

      syncButtonFromMain() {
        if (!this.mainAddBtn || !this.addBtn) return;

        const isDisabled = this.mainAddBtn.hasAttribute("disabled");
        const mainText = this.mainAddBtn.textContent.trim();

        if (isDisabled) {
          this.addBtn.setAttribute("disabled", "disabled");
        } else {
          this.addBtn.removeAttribute("disabled");
        }

        const stickySpan = this.addBtn.querySelector("span") || this.addBtn;
        stickySpan.textContent = mainText;
      }

      handleQuantityUpdate(message) {
        const incoming = message?.data?.quantity;
        if (incoming === undefined || incoming === null) return;
        if (!this.qtyInput) return;

        const qty = parseInt(incoming, 10);
        if (Number.isNaN(qty)) return;

        // set silently so our own input listeners don't re-publish
        this._silentUpdate = true;
        this.qtyInput.value = qty;
        this._silentUpdate = false;
      }

      bindQuantitySync() {
        if (!this.qtyInput) return;

        // Only subscribe once; subscriptions were created in connectedCallback
        // Attach user-event handlers to publish updates
        this.qtyInput.addEventListener("input", () => {
          // if this change was from external sync, skip publishing
          if (this._silentUpdate) return;
          this.syncQuantityToMain();
        });

        this.qtyInput.addEventListener("change", () => {
          if (this._silentUpdate) return;
          this.syncQuantityToMain();
        });
      }

      async addToCart(variantId, quantity = 1) {
        if (!variantId) {
          console.error("product-sticky: missing variant id for addToCart");
          return;
        }

        this.addBtn.setAttribute('aria-disabled', 'true');
        this.addBtn.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData();
        formData.append("id", variantId);
        formData.append("quantity", quantity);

        const cart =
          document.querySelector("cart-notification") || document.querySelector("cart-drawer");

        if (cart) {
          formData.append(
            "sections",
            cart.getSectionsToRender().map((s) => s.id)
          );
          formData.append("sections_url", window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        if (this.addBtn) {
          this.addBtn.disabled = true;
          // show a concise loading state if inner text exists
          const originalText = this.addBtn.textContent;
          try {
            const res = await fetch(`${routes.cart_add_url}`, config);
            const response = await res.json();

            if (response.status) {
              this.handleErrorMessage(response.description || response.message || 'Unable to add to cart');
              setTimeout(() => (this.addBtn.textContent = originalText), 5000);
              return;
            }

            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }

            cart.renderContents(response);
            document.dispatchEvent(new CustomEvent("recheck:quota"));

            setTimeout(() => {
              this.addBtn.textContent = originalText;
              this.addBtn.disabled = false;
            }, 5000);

          } catch (err) {
            console.error("Sticky Add to Cart failed", err);
            this.addBtn.textContent = "Error";
            this.addBtn.disabled = false;
          }
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        if (!this.errorMessageWrapper) {
          this.errorMessageWrapper = document.querySelector('.warning-message');
        }

        if (!this.errorMessageWrapper) return;

        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('p');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
          this.errorMessageWrapper.classList.add('active');

          setTimeout(() => {
            this.addBtn.setAttribute('aria-disabled', 'false');
            this.addBtn.classList.remove('loading');
            this.addBtn.disabled = false;
            this.errorMessageWrapper.classList.remove('active');
          }, 5000);
        }
      }
    }
  );
}




